import { useMemo, useState } from "react";
import { Check, Plus, Layers } from "lucide-react";
import {
  AmenityCategory,
  AMENITY_CATEGORY_LABELS,
} from "@postnatal-care/shared-types";
import {
  useAmenityMasterList,
  type AmenityMasterEntity,
} from "@/hooks/useBusinesses";
import { Skeleton } from "@/components/ui/skeleton";
import { getAmenityIcon } from "./iconMap";
import { AmenityCreateDialog } from "./AmenityCreateDialog";

interface AmenityPickerProps {
  value: string[];
  onChange: (next: string[]) => void;
  allowCreate?: boolean;
}

const CATEGORY_ORDER: AmenityCategory[] = [
  AmenityCategory.ROOM_FACILITIES,
  AmenityCategory.MEDICAL_CARE,
  AmenityCategory.WELLNESS_RECOVERY,
  AmenityCategory.FOOD_NUTRITION,
  AmenityCategory.GENERAL_SERVICES,
  AmenityCategory.OTHER,
];

const CATEGORY_ICONS: Record<AmenityCategory, string> = {
  [AmenityCategory.ROOM_FACILITIES]: "🛏️",
  [AmenityCategory.MEDICAL_CARE]: "🩺",
  [AmenityCategory.WELLNESS_RECOVERY]: "🌿",
  [AmenityCategory.FOOD_NUTRITION]: "🥗",
  [AmenityCategory.GENERAL_SERVICES]: "🔌",
  [AmenityCategory.OTHER]: "✨",
};

export const AmenityPicker = ({
  value,
  onChange,
  allowCreate = true,
}: AmenityPickerProps) => {
  const { data, isLoading, error } = useAmenityMasterList();
  const [createOpen, setCreateOpen] = useState(false);
  const [activeCategoryFilter, setActiveCategoryFilter] =
    useState<string>("ALL");
  const selected = new Set(value);

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(Array.from(next));
  };

  const grouped = useMemo(() => {
    if (!data) return {} as Record<AmenityCategory, AmenityMasterEntity[]>;
    const map = {} as Record<AmenityCategory, AmenityMasterEntity[]>;
    CATEGORY_ORDER.forEach((cat) => {
      map[cat] = [];
    });
    data.forEach((a) => {
      const cat =
        a.category && CATEGORY_ORDER.includes(a.category)
          ? a.category
          : AmenityCategory.GENERAL_SERVICES;
      if (!map[cat]) map[cat] = [];
      map[cat].push(a);
    });
    return map;
  }, [data]);

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-20 w-full" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <p className="text-sm text-destructive">
        Failed to load amenities: {error.message}
      </p>
    );
  }

  const empty = !data?.length;

  const categoriesToDisplay = CATEGORY_ORDER.filter((cat) => {
    if (activeCategoryFilter !== "ALL" && activeCategoryFilter !== cat)
      return false;
    return (
      (grouped[cat] && grouped[cat].length > 0) ||
      (cat === AmenityCategory.GENERAL_SERVICES && allowCreate)
    );
  });

  return (
    <div className="space-y-4">
      {/* Category filter pills */}
      <div className="flex flex-wrap items-center gap-1.5 pb-1 border-b border-border/60">
        <button
          type="button"
          onClick={() => setActiveCategoryFilter("ALL")}
          className={`px-3 py-1 text-xs rounded-full border transition-colors ${
            activeCategoryFilter === "ALL"
              ? "bg-primary text-primary-foreground border-primary font-medium"
              : "bg-muted/40 border-transparent text-muted-foreground hover:bg-muted"
          }`}
        >
          All Categories ({data?.length ?? 0})
        </button>
        {CATEGORY_ORDER.map((cat) => {
          const count = grouped[cat]?.length ?? 0;
          if (count === 0 && activeCategoryFilter !== cat) return null;
          const label = AMENITY_CATEGORY_LABELS[cat]?.en ?? cat;
          return (
            <button
              key={cat}
              type="button"
              onClick={() => setActiveCategoryFilter(cat)}
              className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                activeCategoryFilter === cat
                  ? "bg-primary text-primary-foreground border-primary font-medium"
                  : "bg-muted/40 border-transparent text-muted-foreground hover:bg-muted"
              }`}
            >
              {label} ({count})
            </button>
          );
        })}
      </div>

      {/* Categorized amenity sections */}
      {categoriesToDisplay.map((cat) => {
        const catAmenities = grouped[cat] ?? [];
        if (catAmenities.length === 0 && !allowCreate) return null;
        const labelInfo = AMENITY_CATEGORY_LABELS[cat] ?? { en: cat, ml: "" };

        return (
          <div key={cat} className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                {labelInfo.en}
              </span>
              <span className="text-xs text-muted-foreground font-normal">
                ({labelInfo.ml})
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {catAmenities.map((a) => {
                const Icon = getAmenityIcon(a.iconKey);
                const isSelected = selected.has(a.id);
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => toggle(a.id)}
                    className={`relative flex flex-col items-center gap-1.5 px-3 py-3 rounded-card border text-center transition-colors ${
                      isSelected
                        ? "border-primary bg-primary/5 text-foreground"
                        : "border-border bg-card text-muted-foreground hover:border-primary/50 hover:text-foreground"
                    }`}
                  >
                    {isSelected && (
                      <span className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
                        <Check className="w-3 h-3" />
                      </span>
                    )}
                    <Icon
                      className={`w-6 h-6 ${isSelected ? "text-primary" : ""}`}
                    />
                    <span className="text-xs font-medium leading-tight">
                      {a.name}
                    </span>
                    <span className="text-[10px] leading-tight opacity-70">
                      {a.nameMalayalam}
                    </span>
                  </button>
                );
              })}
              {allowCreate && cat === AmenityCategory.GENERAL_SERVICES && (
                <button
                  type="button"
                  onClick={() => setCreateOpen(true)}
                  className="flex flex-col items-center gap-1.5 px-3 py-3 rounded-card border border-dashed border-border text-muted-foreground hover:border-primary/70 hover:text-primary transition-colors"
                >
                  <Plus className="w-6 h-6" />
                  <span className="text-xs font-medium leading-tight">
                    New amenity
                  </span>
                  <span className="text-[10px] leading-tight opacity-70">
                    &nbsp;
                  </span>
                </button>
              )}
            </div>
          </div>
        );
      })}

      {empty && !allowCreate && (
        <p className="text-sm text-muted-foreground mt-2">
          No amenities configured.
        </p>
      )}

      {allowCreate && (
        <AmenityCreateDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          onCreated={(a) => onChange([...value, a.id])}
        />
      )}
    </div>
  );
};

export default AmenityPicker;
